export type HuntStop = {
  id: string;
  name: string;
  blurb: string;
  lat: number;
  lon: number;
  /** meters above local ground for beacon / photo plate */
  height: number;
  /** Primary reference photo */
  photo: string;
  /** Extra angles for img2threejs / QA (dev Street View or client pack) */
  photos?: string[];
};

/** Origin for local ENU (Château Frontenac plaza). */
export const ORIGIN = {
  lat: 46.81202,
  lon: -71.20542,
  height: 0,
} as const;

const DEV_HEADINGS = [0, 72, 144, 216, 288];

export const HUNT_STOPS: HuntStop[] = [
  {
    id: "chateau-frontenac",
    name: "Château Frontenac",
    blurb: "Start here — the skyline icon above Place d’Armes.",
    lat: 46.81196,
    lon: -71.20536,
    height: 18,
    photo: "/stops/chateau-frontenac.jpg",
  },
  {
    id: "terrasse-dufferin",
    name: "Terrasse Dufferin",
    blurb: "Boardwalk on the cliff edge — look down to the lower town.",
    lat: 46.81255,
    lon: -71.20435,
    height: 10,
    photo: "/stops/terrasse-dufferin.jpg",
  },
  {
    id: "porte-saint-louis",
    name: "Porte Saint-Louis",
    blurb: "Gate through the fortifications.",
    lat: 46.80955,
    lon: -71.21205,
    height: 10,
    photo: "/stops/porte-saint-louis.jpg",
  },
  {
    id: "basilique-notre-dame",
    name: "Basilique-cathédrale Notre-Dame",
    blurb: "Parish heart of Haute-Ville.",
    lat: 46.81355,
    lon: -71.20605,
    height: 16,
    photo: "/stops/basilique-notre-dame.jpg",
  },
  {
    id: "rue-du-tresor",
    name: "Rue du Trésor",
    blurb: "Narrow lane of open-air art.",
    lat: 46.81285,
    lon: -71.20655,
    height: 9,
    photo: "/stops/rue-du-tresor.jpg",
  },
  {
    id: "escalier-casse-cou",
    name: "Escalier Casse-Cou",
    blurb: "Breakneck Stairs down the cliff to Petit-Champlain.",
    lat: 46.81225,
    lon: -71.20355,
    height: 8,
    photo: "/stops/escalier-casse-cou.jpg",
  },
  {
    id: "petit-champlain",
    name: "Rue du Petit-Champlain",
    blurb: "Legendary lower-town street beneath the cliff.",
    lat: 46.81145,
    lon: -71.20285,
    height: 8,
    photo: "/stops/petit-champlain.jpg",
  },
  {
    id: "place-royale",
    name: "Place Royale",
    blurb: "Second iconic plaza — finish the hunt.",
    lat: 46.81295,
    lon: -71.20255,
    height: 8,
    photo: "/stops/place-royale.jpg",
  },
];

type SvManifest = {
  heroes: Array<{
    id: string;
    primary: string | null;
    angles: Array<{ path: string }>;
  }>;
};

/** Overlay DEV Street View Static pack when present (npm run streetview:dev). */
export async function applyDevStreetViewPack(
  stops: HuntStop[],
): Promise<boolean> {
  try {
    const res = await fetch("/dev/streetview/manifest.json");
    if (!res.ok) return false;
    const manifest = (await res.json()) as SvManifest;
    let applied = 0;
    for (const stop of stops) {
      const hero = manifest.heroes.find((h) => h.id === stop.id);
      if (!hero?.primary && !hero?.angles?.length) continue;
      const paths =
        hero.angles?.map((a) => a.path).filter(Boolean) ||
        DEV_HEADINGS.map((h) => `/dev/streetview/${stop.id}/h${h}.jpg`);
      if (hero.primary) {
        stop.photo = hero.primary;
        stop.photos = paths.length ? paths : [hero.primary];
        applied++;
      } else if (paths.length) {
        stop.photo = paths[0];
        stop.photos = paths;
        applied++;
      }
    }
    return applied > 0;
  } catch {
    return false;
  }
}

export const CTA_URL = "https://placestage.com";
